import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Brain,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Calendar,
  Activity,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Flame,
  BarChart3,
  Zap,
  Target,
  Clock,
  Shield,
  Lightbulb,
} from 'lucide-react';

const severityColors = {
  CRITICAL: 'bg-red-500/20 text-red-400 border-red-500/30',
  HIGH: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  LOW: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const priorityColors = {
  URGENT: 'bg-red-500/20 text-red-400 border-red-500/30',
  HIGH: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  LOW: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const burnStatusColors = {
  CRITICAL: 'bg-red-500/20 text-red-400',
  WARNING: 'bg-orange-500/20 text-orange-400',
  ON_TRACK: 'bg-green-500/20 text-green-400',
  UNDER_BUDGET: 'bg-blue-500/20 text-blue-400',
};

const burnoutColors = {
  CRITICAL: 'bg-red-500/20 text-red-400',
  HIGH: 'bg-orange-500/20 text-orange-400',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400',
  LOW: 'bg-green-500/20 text-green-400',
};

const trendIcons = {
  IMPROVING: TrendingUp,
  STABLE: Activity,
  DETERIORATING: TrendingDown,
};

export default function AIIntelligencePage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [fundingBurn, setFundingBurn] = useState(null);
  const [burnoutData, setBurnoutData] = useState(null);
  const [rosteringSuggestions, setRosteringSuggestions] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [patternDays, setPatternDays] = useState('30');

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai-intelligence/dashboard');
      setDashboard(res.data.data);
    } catch (err) {
      console.error('Failed to fetch AI dashboard:', err);
      toast.error('Failed to load AI Intelligence dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatterns = async () => {
    try {
      const res = await api.get(`/ai-intelligence/incident-patterns?days=${patternDays}`);
      setPatterns(res.data.data);
    } catch (err) {
      console.error('Failed to fetch patterns:', err);
    }
  };

  const fetchFundingBurn = async () => {
    try {
      const res = await api.get('/ai-intelligence/funding-burn/dashboard');
      setFundingBurn(res.data.data);
    } catch (err) {
      console.error('Failed to fetch funding burn:', err);
    }
  };

  const fetchBurnout = async () => {
    try {
      const res = await api.get('/ai-intelligence/burnout/dashboard');
      setBurnoutData(res.data.data);
    } catch (err) {
      console.error('Failed to fetch burnout data:', err);
    }
  };

  const fetchRosteringSuggestions = async () => {
    try {
      const res = await api.get('/ai-intelligence/rostering-suggestions?days_ahead=7');
      setRosteringSuggestions(res.data.data);
    } catch (err) {
      console.error('Failed to fetch rostering suggestions:', err);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchPatterns();
    fetchFundingBurn();
    fetchBurnout();
    fetchRosteringSuggestions();
  }, []);

  useEffect(() => {
    fetchPatterns();
  }, [patternDays]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-intelligence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-400" />
            AI Intelligence Layer
          </h1>
          <p className="text-muted-foreground mt-1">
            Predictive analytics, pattern detection, and smart recommendations
          </p>
        </div>
        <Button 
          onClick={() => {
            fetchDashboard();
            fetchPatterns();
            fetchFundingBurn();
            fetchBurnout();
            fetchRosteringSuggestions();
          }}
          variant="outline"
          data-testid="refresh-ai-dashboard"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Analysis
        </Button>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-950/40 to-purple-900/20 border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Incident Patterns</p>
                  <p className="text-3xl font-bold mt-1">{dashboard.incident_patterns?.total_patterns || 0}</p>
                  <p className="text-xs text-purple-400 mt-1">
                    {dashboard.incident_patterns?.critical || 0} critical
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-950/40 to-red-900/20 border-red-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Burnout Alerts</p>
                  <p className="text-3xl font-bold mt-1">{dashboard.burnout_alerts?.summary?.intervention_needed || 0}</p>
                  <p className="text-xs text-red-400 mt-1">
                    {dashboard.burnout_alerts?.critical_count || 0} critical
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Flame className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-950/40 to-orange-900/20 border-orange-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Funding At Risk</p>
                  <p className="text-3xl font-bold mt-1">{dashboard.funding_alerts?.at_risk_count || 0}</p>
                  <p className="text-xs text-orange-400 mt-1">
                    {dashboard.funding_alerts?.summary?.critical_count || 0} critical
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-950/40 to-blue-900/20 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Roster Suggestions</p>
                  <p className="text-3xl font-bold mt-1">{dashboard.rostering_suggestions?.total || 0}</p>
                  <p className="text-xs text-blue-400 mt-1">
                    {dashboard.rostering_suggestions?.urgent || 0} urgent
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">Incident Patterns</TabsTrigger>
          <TabsTrigger value="funding" data-testid="tab-funding">Funding Burn</TabsTrigger>
          <TabsTrigger value="burnout" data-testid="tab-burnout">Staff Burnout</TabsTrigger>
          <TabsTrigger value="rostering" data-testid="tab-rostering">Smart Rostering</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Incident Patterns */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                  Detected Patterns
                </CardTitle>
                <CardDescription>Incident patterns requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.incident_patterns?.patterns?.slice(0, 5).map((pattern, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <Badge className={severityColors[pattern.severity]}>{pattern.severity}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{pattern.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">{pattern.action_required}</p>
                      </div>
                    </div>
                  )) || (
                    <p className="text-muted-foreground text-sm">No patterns detected</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Rostering Suggestions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-400" />
                  Rostering Suggestions
                </CardTitle>
                <CardDescription>AI-powered schedule recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.rostering_suggestions?.suggestions?.slice(0, 5).map((suggestion, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <Badge className={priorityColors[suggestion.priority]}>{suggestion.priority}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{suggestion.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{suggestion.description}</p>
                      </div>
                    </div>
                  )) || (
                    <p className="text-muted-foreground text-sm">No suggestions available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Incident Patterns Tab */}
        <TabsContent value="patterns" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={patternDays} onValueChange={setPatternDays}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Time Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {patterns && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">
                  {patterns.total_patterns} patterns detected
                </span>
              </div>
            )}
          </div>

          {/* Pattern Type Breakdown */}
          {patterns && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(patterns.by_type || {}).map(([type, count]) => (
                <Card key={type} className="bg-zinc-900/50">
                  <CardContent className="pt-4 pb-3 text-center">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground mt-1">{type.replace(/_/g, ' ')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Patterns Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Detected Patterns</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Count</TableHead>
                    <TableHead>Action Required</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patterns?.patterns?.map((pattern, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge className={severityColors[pattern.severity]}>{pattern.severity}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{pattern.pattern_type.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="max-w-md truncate">{pattern.description}</TableCell>
                      <TableCell>{pattern.occurrence_count}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{pattern.action_required}</TableCell>
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No patterns detected in the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funding Burn Tab */}
        <TabsContent value="funding" className="space-y-6">
          {fundingBurn && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Participants</p>
                    <p className="text-2xl font-bold mt-1">{fundingBurn.summary?.total_participants || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-950/30 border-red-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-red-400">Critical</p>
                    <p className="text-2xl font-bold mt-1 text-red-400">{fundingBurn.summary?.status_breakdown?.CRITICAL || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-950/30 border-orange-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-orange-400">Warning</p>
                    <p className="text-2xl font-bold mt-1 text-orange-400">{fundingBurn.summary?.status_breakdown?.WARNING || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-950/30 border-blue-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-blue-400">Under Budget</p>
                    <p className="text-2xl font-bold mt-1 text-blue-400">{fundingBurn.summary?.status_breakdown?.UNDER_BUDGET || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* At Risk Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-400" />
                    At Risk Participants
                  </CardTitle>
                  <CardDescription>Participants whose funding may exhaust before plan end</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Participant</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Total Budget</TableHead>
                        <TableHead>Spent</TableHead>
                        <TableHead>Burn Rate</TableHead>
                        <TableHead>Days Left</TableHead>
                        <TableHead>Velocity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fundingBurn.at_risk?.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{item.participant_name}</TableCell>
                          <TableCell>
                            <Badge className={burnStatusColors[item.burn_status]}>{item.burn_status}</Badge>
                          </TableCell>
                          <TableCell>${item.total_budget?.toLocaleString()}</TableCell>
                          <TableCell>${item.spent_to_date?.toLocaleString()}</TableCell>
                          <TableCell>${item.burn_rate_daily?.toFixed(2)}/day</TableCell>
                          <TableCell>{item.days_until_exhaustion}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress 
                                value={Math.min(item.spending_velocity, 150)} 
                                max={150}
                                className="w-20 h-2"
                              />
                              <span className={item.spending_velocity > 100 ? 'text-red-400' : 'text-green-400'}>
                                {item.spending_velocity?.toFixed(0)}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )) || (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            No participants at risk
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Under Utilised */}
              {fundingBurn.under_utilised?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingDown className="h-5 w-5 text-blue-400" />
                      Under-Utilised Funding
                    </CardTitle>
                    <CardDescription>Participants who may not be receiving adequate support</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Participant</TableHead>
                          <TableHead>Total Budget</TableHead>
                          <TableHead>Spent</TableHead>
                          <TableHead>Velocity</TableHead>
                          <TableHead>Recommendation</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fundingBurn.under_utilised?.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{item.participant_name}</TableCell>
                            <TableCell>${item.total_budget?.toLocaleString()}</TableCell>
                            <TableCell>${item.spent_to_date?.toLocaleString()}</TableCell>
                            <TableCell>{item.spending_velocity?.toFixed(0)}%</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.recommendations?.[0]}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Staff Burnout Tab */}
        <TabsContent value="burnout" className="space-y-6">
          {burnoutData && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-zinc-900/50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Staff</p>
                    <p className="text-2xl font-bold mt-1">{burnoutData.summary?.total_staff || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-950/30 border-red-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-red-400">Critical</p>
                    <p className="text-2xl font-bold mt-1 text-red-400">{burnoutData.summary?.risk_breakdown?.CRITICAL || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-950/30 border-orange-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-orange-400">High Risk</p>
                    <p className="text-2xl font-bold mt-1 text-orange-400">{burnoutData.summary?.risk_breakdown?.HIGH || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-950/30 border-yellow-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-yellow-400">Medium</p>
                    <p className="text-2xl font-bold mt-1 text-yellow-400">{burnoutData.summary?.risk_breakdown?.MEDIUM || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-950/30 border-green-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-green-400">Low Risk</p>
                    <p className="text-2xl font-bold mt-1 text-green-400">{burnoutData.summary?.risk_breakdown?.LOW || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Critical Alerts */}
              {burnoutData.critical_alerts?.length > 0 && (
                <Card className="border-red-500/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-400">
                      <AlertCircle className="h-5 w-5" />
                      Critical Burnout Alerts
                    </CardTitle>
                    <CardDescription>Immediate intervention required</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {burnoutData.critical_alerts.map((alert, i) => (
                        <div key={i} className="p-4 rounded-lg bg-red-950/20 border border-red-500/30">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Users className="h-5 w-5 text-red-400" />
                              <span className="font-medium">{alert.staff_name}</span>
                            </div>
                            <Badge className="bg-red-500/20 text-red-400">Score: {alert.burnout_score}</Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Weekly Hours</p>
                              <p className="font-medium">{alert.recent_workload?.weekly_hours || 0}h</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Consecutive Days</p>
                              <p className="font-medium">{alert.recent_workload?.consecutive_days || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Incidents (30d)</p>
                              <p className="font-medium">{alert.recent_workload?.incidents_30d || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Cancellations</p>
                              <p className="font-medium">{alert.recent_workload?.cancellations_30d || 0}</p>
                            </div>
                          </div>
                          {alert.indicators?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-red-500/20">
                              <p className="text-xs text-muted-foreground mb-2">Indicators:</p>
                              <div className="flex flex-wrap gap-2">
                                {alert.indicators.map((ind, j) => (
                                  <Badge key={j} variant="outline" className="text-xs">
                                    {ind.indicator}: {ind.value}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {alert.recommendations?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-red-500/20">
                              <p className="text-xs text-muted-foreground mb-2">Recommendations:</p>
                              <ul className="text-sm space-y-1">
                                {alert.recommendations.map((rec, j) => (
                                  <li key={j} className="flex items-center gap-2">
                                    <Lightbulb className="h-3 w-3 text-yellow-400" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* High Risk */}
              {burnoutData.high_risk?.length > 0 && (
                <Card className="border-orange-500/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-orange-400">
                      <AlertTriangle className="h-5 w-5" />
                      High Risk Staff
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staff Member</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Weekly Hours</TableHead>
                          <TableHead>Consecutive Days</TableHead>
                          <TableHead>Indicators</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {burnoutData.high_risk.map((alert, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{alert.staff_name}</TableCell>
                            <TableCell>
                              <Badge className={burnoutColors[alert.burnout_risk_level]}>{alert.burnout_score}</Badge>
                            </TableCell>
                            <TableCell>{alert.recent_workload?.weekly_hours || 0}h</TableCell>
                            <TableCell>{alert.recent_workload?.consecutive_days || 0}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {alert.indicators?.slice(0, 2).map((ind, j) => (
                                  <Badge key={j} variant="outline" className="text-xs">{ind.indicator}</Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Smart Rostering Tab */}
        <TabsContent value="rostering" className="space-y-6">
          {rosteringSuggestions && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Suggestions</p>
                    <p className="text-2xl font-bold mt-1">{rosteringSuggestions.total_suggestions || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-950/30 border-red-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-red-400">Urgent</p>
                    <p className="text-2xl font-bold mt-1 text-red-400">{rosteringSuggestions.by_priority?.URGENT || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-950/30 border-orange-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-orange-400">High Priority</p>
                    <p className="text-2xl font-bold mt-1 text-orange-400">{rosteringSuggestions.by_priority?.HIGH || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-950/30 border-blue-500/30">
                  <CardContent className="pt-4">
                    <p className="text-sm text-blue-400">Coverage Issues</p>
                    <p className="text-2xl font-bold mt-1 text-blue-400">{rosteringSuggestions.by_type?.COVERAGE || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Suggestions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-400" />
                    AI Rostering Suggestions
                  </CardTitle>
                  <CardDescription>Smart recommendations for optimal scheduling</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {rosteringSuggestions.suggestions?.map((suggestion, i) => (
                      <div key={i} className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Badge className={priorityColors[suggestion.priority]}>{suggestion.priority}</Badge>
                            <Badge variant="outline">{suggestion.suggestion_type}</Badge>
                          </div>
                        </div>
                        <h4 className="font-medium mb-2">{suggestion.title}</h4>
                        <p className="text-sm text-muted-foreground mb-3">{suggestion.description}</p>
                        
                        {suggestion.suggested_staff?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-muted-foreground mb-2">Suggested Staff:</p>
                            <div className="flex flex-wrap gap-2">
                              {suggestion.suggested_staff.map((staff, j) => (
                                <Badge key={j} variant="outline" className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {staff.staff_name}
                                  {staff.burnout_level && (
                                    <span className={`ml-1 text-xs ${burnoutColors[staff.burnout_level]?.split(' ')[1]}`}>
                                      ({staff.burnout_level})
                                    </span>
                                  )}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {suggestion.rationale?.length > 0 && (
                          <div className="pt-3 border-t border-zinc-700">
                            <p className="text-xs text-muted-foreground mb-2">Rationale:</p>
                            <ul className="text-sm space-y-1">
                              {suggestion.rationale.map((reason, j) => (
                                <li key={j} className="flex items-center gap-2 text-muted-foreground">
                                  <ChevronRight className="h-3 w-3" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )) || (
                      <p className="text-center text-muted-foreground py-8">
                        No rostering suggestions at this time
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
